import React from 'react';
import { Button, Space, Table, Tag, Typography } from 'antd';
import { useNavigation } from '@refinedev/core';
import type { ColumnsType } from 'antd/es/table';
import type { PagedListState } from '@/hooks/usePagedList';
import type { CardGenerateAuditItem, CardGenerateAuditResponse } from '@/types';
import { buildLabel, rarityName } from '@/utils/cardGenerateAudit';
import { formatIstDateTime } from '@/utils/analyticsWindow';
import { ListBody, LoadMoreFooter } from './ListParts';

const { Text } = Typography;

export type CardGeneratesSummary = Omit<CardGenerateAuditResponse, 'rows'>;

export const CardGeneratesTab: React.FC<{
  state: PagedListState<CardGenerateAuditItem, CardGeneratesSummary>;
}> = ({ state }) => {
  const { show } = useNavigation();
  const summary = state.list?.summary;
  const columns: ColumnsType<CardGenerateAuditItem> = [
    {
      title: 'Player',
      dataIndex: 'userDisplayName',
      render: (name: string | null | undefined, record) => (
        <Button type="link" size="small" onClick={() => show('players', record.userId)}>
          {name || record.userId.slice(0, 8)}
        </Button>
      ),
    },
    { title: 'IST day', dataIndex: 'istDay', width: 110 },
    {
      title: 'Rarity',
      dataIndex: 'rarity',
      width: 100,
      render: (r: number) => rarityName(r),
    },
    {
      title: 'Chosen by',
      dataIndex: 'chosenByClient',
      width: 110,
      render: (c: boolean) => (c ? <Tag color="red">Client</Tag> : <Tag>Server roll</Tag>),
    },
    {
      title: 'Calls',
      dataIndex: 'callCount',
      width: 80,
      sorter: (a, b) => a.callCount - b.callCount,
    },
    { title: 'With drop', dataIndex: 'withDropCount', width: 100 },
    { title: 'Rejected', dataIndex: 'rejectedCount', width: 90 },
    {
      title: 'Build',
      width: 100,
      render: (_: unknown, r) => buildLabel(r),
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      width: 100,
      render: (p: string | null) => p ?? 'not sent',
    },
    {
      title: 'App version',
      dataIndex: 'appVersion',
      width: 110,
      render: (v: string | null) => v ?? 'not sent',
    },
    {
      title: 'Last call (IST)',
      dataIndex: 'lastAt',
      width: 150,
      render: (d: string) => formatIstDateTime(new Date(d)),
    },
  ];

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Every call to POST /v1/player-cards/generate for a Rare, Epic or Legendary card is recorded,
        and so is every rejected Common call. &quot;Rejected&quot; counts calls the server refused,
        for example a missing card-drop token when drops are enforced. Recording blocks nothing by
        itself. One row per player, IST day, rarity and who chose it. &quot;Client&quot; means the
        request body carried a rarity, so the client picked it; &quot;Server roll&quot; means the
        server rolled it. The platform and version headers are as the client sent them and can be
        faked.
      </Text>
      <ListBody state={state}>
        {summary && (
          <>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {summary.totalCalls.toLocaleString()} calls from{' '}
              {summary.uniqueUsers.toLocaleString()} players
            </Text>
            {summary.byRarity.length > 0 && (
              <Space wrap style={{ marginBottom: 12 }}>
                {[0, 1, 2, 3, ...summary.byRarity.map((b) => b.rarity)]
                  .filter((r, i, all) => all.indexOf(r) === i)
                  .filter((r) => summary.byRarity.some((b) => b.rarity === r))
                  .map((r) => {
                    const calls = (chosen: boolean) =>
                      summary.byRarity
                        .filter((b) => b.rarity === r && b.chosenByClient === chosen)
                        .reduce((n, b) => n + b.calls, 0);
                    return (
                      <Tag key={r} color={calls(true) > 0 ? 'volcano' : 'default'}>
                        {rarityName(r)}: {calls(true).toLocaleString()} client-chosen /{' '}
                        {calls(false).toLocaleString()} rolled
                      </Tag>
                    );
                  })}
              </Space>
            )}
            {summary.byBuild.length > 0 && (
              <Table<CardGenerateAuditResponse['byBuild'][number]>
                title={() => <Text strong>By build</Text>}
                dataSource={summary.byBuild}
                rowKey={(b) => `${b.build}:${b.platform ?? ''}`}
                size="small"
                pagination={{ pageSize: 5, size: 'small', hideOnSinglePage: true }}
                style={{ marginBottom: 16, maxWidth: 560 }}
                columns={[
                  { title: 'Build', dataIndex: 'build' },
                  {
                    title: 'Platform',
                    dataIndex: 'platform',
                    render: (p: string | null) => p ?? 'not sent',
                  },
                  {
                    title: 'Calls',
                    dataIndex: 'calls',
                    render: (n: number) => n.toLocaleString(),
                  },
                  {
                    title: 'Players',
                    dataIndex: 'uniqueUsers',
                    render: (n: number) => n.toLocaleString(),
                  },
                ]}
              />
            )}
          </>
        )}
        <Table<CardGenerateAuditItem>
          dataSource={state.list?.rows ?? []}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: 1200 }}
          columns={columns}
        />
        <LoadMoreFooter state={state} noun="rows" />
      </ListBody>
    </div>
  );
};
